import React, { Suspense, lazy, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SystemUpdateAltRoundedIcon from '@mui/icons-material/SystemUpdateAltRounded';
import { usePWA } from '../hooks/usePWA';
import { useOfflineQueueStore } from '../stores/offlineQueueStore';
import type { PWAUpdateCompanionCast } from './PWAUpdateCompanion';

const PWAUpdateCompanion = lazy(() =>
  import('./PWAUpdateCompanion').then((module) => ({
    default: module.PWAUpdateCompanion,
  })),
);

/**
 * Renders:
 *  1. A persistent "You're offline" banner when disconnected
 *  2. An "Update available" prompt when a new SW is ready
 *  3. An "Offline ready" confirmation toast
 *  4. A mobile bottom-sheet install prompt (Android native + iOS instructions)
 */
export const PWAPrompt: React.FC = () => {
  const { needRefresh, offlineReady, applyUpdate, isApplyingUpdate, close } = usePWA();
  const isOnline = useOfflineQueueStore((s) => s.isOnline);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const previousCompanionCast = useRef<PWAUpdateCompanionCast>();
  const rememberCompanionCast = useCallback((cast: PWAUpdateCompanionCast) => {
    previousCompanionCast.current = cast;
  }, []);

  return (
    <>
      {/* ── Offline banner ─────────────────────────────────────── */}
      <Snackbar open={!isOnline} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="warning" variant="filled" sx={{ width: '100%', fontWeight: 600 }}>
          You're offline — PetroNexus will queue field captures and sync them when you reconnect.
        </Alert>
      </Snackbar>

      {/* ── App updated prompt ─────────────────────────────────── */}
      <Snackbar
        open={needRefresh}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          left: { xs: 16, sm: '50%' },
          right: { xs: 16, sm: 'auto' },
          bottom: { xs: 18, sm: 28 },
          transform: { sm: 'translateX(-50%)' },
        }}
      >
        <Paper
          elevation={0}
          role="status"
          aria-live="polite"
          sx={{
            width: { xs: '100%', sm: 640 },
            overflow: 'hidden',
            borderRadius: 3,
            border: `1px solid ${alpha(
              isDark ? theme.palette.common.white : theme.palette.common.black,
              isDark ? 0.14 : 0.08,
            )}`,
            bgcolor: alpha(theme.palette.background.paper, isDark ? 0.92 : 0.96),
            color: 'text.primary',
            boxShadow: isDark
              ? `0 24px 70px ${alpha(theme.palette.common.black, 0.56)}`
              : `0 20px 55px ${alpha(theme.palette.grey[900], 0.16)}`,
            backdropFilter: 'blur(20px) saturate(160%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ p: { xs: 1.75, sm: 2 } }}
          >
            {needRefresh ? (
              <Suspense fallback={<DefaultUpdatePromptContent isDark={isDark} />}>
                <PWAUpdateCompanion
                  previousCast={previousCompanionCast.current}
                  onCast={rememberCompanionCast}
                />
              </Suspense>
            ) : (
              <DefaultUpdatePromptContent isDark={isDark} />
            )}

            <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-end', sm: 'center' }}>
              <Button
                variant="contained"
                disableElevation
                onClick={applyUpdate}
                disabled={isApplyingUpdate}
                startIcon={
                  isApplyingUpdate ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : (
                    <SystemUpdateAltRoundedIcon fontSize="small" />
                  )
                }
                sx={{
                  borderRadius: 999,
                  px: 2.1,
                  py: 0.9,
                  fontWeight: 900,
                  bgcolor: theme.palette.warning.main,
                  color: theme.palette.getContrastText(theme.palette.warning.main),
                  '&:hover': {
                    bgcolor: theme.palette.warning.dark,
                  },
                }}
              >
                {isApplyingUpdate ? 'Reloading' : 'Reload'}
              </Button>

              <IconButton
                size="small"
                aria-label="Dismiss update reminder"
                onClick={close}
                sx={{
                  color: 'text.secondary',
                  bgcolor: alpha(
                    isDark ? theme.palette.common.white : theme.palette.common.black,
                    isDark ? 0.06 : 0.04,
                  ),
                  '&:hover': {
                    bgcolor: alpha(
                      isDark ? theme.palette.common.white : theme.palette.common.black,
                      isDark ? 0.1 : 0.07,
                    ),
                  },
                }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Paper>
      </Snackbar>

      {/* ── Offline ready confirmation ─────────────────────────── */}
      <Snackbar
        open={offlineReady} autoHideDuration={5000} onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
          PetroNexus is ready to work offline!
        </Alert>
      </Snackbar>

    </>
  );
};

function DefaultUpdatePromptContent({ isDark }: { isDark: boolean }) {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          display: 'grid',
          placeItems: 'center',
          flex: '0 0 auto',
          color: theme.palette.warning.dark,
          bgcolor: alpha(theme.palette.warning.main, isDark ? 0.18 : 0.16),
          border: `1px solid ${alpha(theme.palette.warning.main, isDark ? 0.28 : 0.22)}`,
        }}
      >
        <InfoOutlinedIcon fontSize="small" />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
          New PetroNexus version ready
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 0.35,
            color: 'text.secondary',
            lineHeight: 1.45,
          }}
        >
          Reload to switch to the latest build before starting a fresh workflow.
        </Typography>
      </Box>
    </Stack>
  );
}
